/*
__/\\\\\\\\\\\\\\\__/\\\\\\\\\\\\\\\_____/\\\\\\\\\____        
 _\///////\\\/////__\///////\\\/////____/\\\\\\\\\\\\\__       
  _______\/\\\_____________\/\\\________/\\\/////////\\\_      
   _______\/\\\_____________\/\\\_______\/\\\_______\/\\\_     
    _______\/\\\_____________\/\\\_______\/\\\\\\\\\\\\\\\_    
     _______\/\\\_____________\/\\\_______\/\\\/////////\\\_   
      _______\/\\\_____________\/\\\_______\/\\\_______\/\\\_  
       _______\/\\\_____________\/\\\_______\/\\\_______\/\\\_ 
        _______\///______________\///________\///________\///__
            
            COPYRIGHT TACTICAL TRANSPORTATION ADVISORS, INC. 
            ALL RIGHTS RESERVED.
*/

import { useRef, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import { pdfjs } from "react-pdf";
import CustomButton from "../../../components/CustomButton";
import { setEVerify } from "../../../services/DashboardService";
import { AppGlobals } from "../../../App";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const E_VERIFY_REGEX = /E-Verify Case Number:\s*([A-Za-z0-9]+)/;

async function parsePdf(file) {
  const arrayBuffer = await file.arrayBuffer();
  const uint8Array = new Uint8Array(arrayBuffer);

  // Convert to full data URL (mime type + base64)
  let binary = "";
  for (let i = 0; i < uint8Array.byteLength; i++) {
    binary += String.fromCharCode(uint8Array[i]);
  }
  const base64 = `data:application/pdf;base64,${btoa(binary)}`;

  // Parse case number from PDF text
  const loadingTask = pdfjs.getDocument({ data: uint8Array });
  const doc = await loadingTask.promise;

  let caseNumber = "";
  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i);
    const textContent = await page.getTextContent();
    const pageText = textContent.items
      .filter((item) => "str" in item)
      .map((item) => item.str)
      .join(" ");

    const match = pageText.match(E_VERIFY_REGEX);
    if (match) {
      caseNumber = match[1];
      break;
    }
  }

  return { base64, caseNumber };
}

export default function EVerifyModal({ documentRequest, hideModal, onSuccess }) {
  const [isParsing, setIsParsing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [caseNumber, setCaseNumber] = useState("");
  const [base64, setBase64] = useState("");
  const [parseError, setParseError] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const fileInputRef = useRef(null);

  const employeeName = `${documentRequest.employee?.firstName ?? ""} ${documentRequest.employee?.lastName ?? ""}`.trim();

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    setParseError("");
    setCaseNumber("");
    setBase64("");
    setShowPreview(false);
    setIsParsing(true);

    try {
      const { base64: parsedBase64, caseNumber: parsedCaseNumber } = await parsePdf(file);
      setBase64(parsedBase64);
      if (parsedCaseNumber) {
        setCaseNumber(parsedCaseNumber);
      } else {
        setParseError("Could not find an E-Verify Case Number in the uploaded PDF. You can enter it manually below.");
      }
    } catch {
      setParseError("Failed to parse PDF. You can enter the case number manually below.");
    } finally {
      setIsParsing(false);
    }
  }

  async function handleSubmit() {
    if (!caseNumber.trim()) return;

    setIsSubmitting(true);
    const response = await setEVerify(documentRequest.tcCompanyUserIdentifier, caseNumber.trim(), base64);
    if (response.status == 200) {
      onSuccess();
      hideModal();
    } else {
      AppGlobals.alert("danger", response.message);
    }
    setIsSubmitting(false);
  }

  return (
    <>
      <Modal show={showPreview} onHide={() => setShowPreview(false)} fullscreen>
        <Modal.Header closeButton>
          <Modal.Title>E-Verify PDF Preview — {employeeName}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0 }}>
          <iframe src={base64} style={{ width: "100%", height: "100%", border: "none" }} title="E-Verify PDF Preview" />
        </Modal.Body>
      </Modal>

      <Modal.Header closeButton>
        <Modal.Title>Assign E-Verify — {employeeName}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div>
          <Form.Label>Upload E-Verify PDF</Form.Label>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Form.Control ref={fileInputRef} type="file" accept="application/pdf" onChange={handleFileChange} disabled={isParsing || isSubmitting} />
            {base64 && (
              <Button variant="outline-secondary" size="sm" style={{ whiteSpace: "nowrap" }} onClick={() => setShowPreview(true)} disabled={isParsing || isSubmitting}>
                <FontAwesomeIcon icon={faEye} /> Preview
              </Button>
            )}
          </div>
          <Form.Text muted>The E-Verify case number will be automatically extracted from the uploaded PDF when possible. Please verify the number before saving.</Form.Text>
        </div>

        {isParsing && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#6c757d" }}>
            <Spinner size="sm" />
            <span>Parsing PDF...</span>
          </div>
        )}

        {parseError && <p style={{ color: "#dc3545", margin: 0, fontSize: "0.875em" }}>{parseError}</p>}

        <div>
          <Form.Label>E-Verify Case Number</Form.Label>
          <Form.Control type="text" maxLength={15} placeholder="e.g. 2013226155522CL " value={caseNumber} onChange={(e) => setCaseNumber(e.target.value)} disabled={isSubmitting} />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <CustomButton label="Cancel" variant="outline-secondary" onClick={hideModal} disabled={isSubmitting} />
        <CustomButton label="Save E-Verify" isLoading={isSubmitting} disabled={!caseNumber.trim() || !base64 || isParsing} onClick={handleSubmit} />
      </Modal.Footer>
    </>
  );
}
